// @ts-nocheck
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  UserPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Context from '../../context/Context';
import { fetchUsers } from '../../context/forms/FormsAction';
import { URL } from '../../service/Service';
import Header from '../../components/TitleAndSubtitle';
import Breadcrumb from '../../components/Breadcrumb';
import Spinner from '../../components/Spinner';

const ROWS_PER_PAGE = 10;
const roleLabels = {
  ADMIN: 'Administrador',
  COMPANY: 'Empresa',
  USER: 'Utilizador',
};

const AdminUsers = () => {
  const { state, dispatch } = useContext(Context);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [includeForms, setIncludeForms] = useState(false);
  const [notice, setNotice] = useState(null);

  const users = useMemo(
    () => Object.values(state.users?.data || {}).reverse(),
    [state.users?.data],
  );
  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) =>
      String(user.userEmail || '').toLowerCase().includes(query)
      || String(user.companyID || '').toLowerCase().includes(query)
      || String(roleLabels[user.userRole] || user.userRole || '').toLowerCase().includes(query));
  }, [users, searchTerm]);
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ROWS_PER_PAGE));
  const currentRows = filteredUsers.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE,
  );

  useEffect(() => {
    fetchUsers(`${URL}/api/admin/users/all`, {}, dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const counts = useMemo(() => ({
    active: users.filter((user) => !user.deleted).length,
    admins: users.filter((user) => user.userRole === 'ADMIN').length,
    companies: users.filter((user) => user.userRole === 'COMPANY').length,
  }), [users]);

  const handleRoleChange = async (role, email) => {
    try {
      const token = sessionStorage.getItem('jwt');
      const response = await fetch(`${URL}/api/admin/users/changeRole`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ role, email }),
      });
      if (!response.ok) throw new Error(await response.text());
      fetchUsers(`${URL}/api/admin/users/all`, {}, dispatch);
      setNotice({ type: 'success', message: 'Função atualizada.' });
    } catch {
      setNotice({ type: 'error', message: 'Não foi possível alterar a função.' });
    }
  };

  const handleConfirmStatus = async () => {
    if (!selectedUser) return;
    try {
    const response = await fetch(`${URL}/api/admin/users/${selectedUser.id}?includeForms=${includeForms}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${sessionStorage.getItem('jwt')}`, 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(await response.text());
    const result = await response.json();
    setSelectedUser(null);
    setIncludeForms(false);
    await fetchUsers(`${URL}/api/admin/users/all`, {}, dispatch);
    setNotice({type:'success',message:`Conta ${result.blocked?'inativada':'ativada'}${includeForms?` e ${result.formsChanged} formulário(s) atualizado(s)`:''}.`});
    } catch (exception) { setNotice({type:'error',message:exception.message||'Não foi possível alterar o acesso.'}); }
  };

  const pages = [
    { name: 'Dashboard', href: '/admin/dashboard', current: false },
    { name: 'Utilizadores', href: '/admin/users', current: true },
  ];

  if (state.users?.loading) return <Spinner />;

  return (
    <main className="min-h-[calc(100vh-5rem)] bg-gradient-to-br from-slate-50 via-white to-emerald-50/50 px-4 py-7 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <Breadcrumb pages={pages} />
        <div className="mt-6">
          <Header
            eyebrow="Administração"
            title="Utilizadores"
            description="Consulte contas, altere permissões e controle o acesso à plataforma."
            actions={(
              <button
                type="button"
                onClick={() => navigate('/adminregisterpage')}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-green-cl px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-green-eupago"
              >
                <UserPlusIcon className="h-5 w-5" />
                Novo administrador
              </button>
            )}
          />
        </div>

        <section className="mb-6 grid gap-4 sm:grid-cols-3">
          {[
            { label: 'Contas ativas', value: counts.active, icon: UserGroupIcon, tone: 'bg-emerald-50 text-emerald-700' },
            { label: 'Administradores', value: counts.admins, icon: ShieldCheckIcon, tone: 'bg-sky-50 text-sky-700' },
            { label: 'Empresas', value: counts.companies, icon: BuildingOffice2Icon, tone: 'bg-violet-50 text-violet-700' },
          ].map(({ label, value, icon: Icon, tone }) => (
            <article key={label} className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
                  <p className="mt-2 text-3xl font-extrabold text-slate-950">{value}</p>
                </div>
                <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tone}`}>
                  <Icon className="h-5 w-5" />
                </span>
              </div>
            </article>
          ))}
        </section>

        <section className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:p-6 lg:flex-row lg:items-center lg:justify-between">
            <label className="relative block w-full lg:max-w-xl">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Procurar por email, conta ou função"
                className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-12 pr-4 text-sm outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-100"
              />
            </label>
            <p className="text-sm text-slate-500">
              <strong className="text-slate-800">{filteredUsers.length}</strong> {filteredUsers.length === 1 ? 'resultado' : 'resultados'}
            </p>
          </div>

          {state.users?.error && (
            <div className="m-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              Não foi possível carregar os utilizadores.
            </div>
          )}

          {currentRows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    <th className="px-6 py-4">Utilizador</th>
                    <th className="px-6 py-4">Função</th>
                    <th className="px-6 py-4">N.º de conta</th>
                    <th className="px-6 py-4">Estado da conta</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentRows.map((user) => (
                    <tr key={user.id} className="transition hover:bg-emerald-50/30">
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-green-cl text-sm font-bold text-white">
                            {String(user.userEmail || 'U').slice(0, 1).toUpperCase()}
                          </span>
                          <div>
                            <p className="font-bold text-slate-900">{user.userEmail || 'Sem email'}</p>
                            <p className="mt-1 font-mono text-xs text-slate-400">{user.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <select
                          value={user.userRole}
                          onChange={(event) => handleRoleChange(event.target.value, user.userEmail)}
                          className="h-10 min-w-[170px] rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                        >
                          <option value="USER">Utilizador</option>
                          <option value="COMPANY">Empresa</option>
                          <option value="ADMIN">Administrador</option>
                        </select>
                      </td>
                      <td className="px-6 py-5 text-sm font-medium text-slate-600">
                        {user.companyID || '—'}
                      </td>
                      <td className="px-6 py-5">
                        <button
                          type="button"
                          onClick={() => {setSelectedUser(user);setIncludeForms(false)}}
                          className={`inline-flex min-w-24 items-center justify-center rounded-full px-3 py-2 text-xs font-bold transition ${
                            user.deleted
                              ? 'bg-red-50 text-red-700 hover:bg-red-100'
                              : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                          }`}
                        >
                          {user.deleted ? 'Inativa' : 'Ativa'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-16 text-center">
              <UserGroupIcon className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-4 font-bold text-slate-800">Nenhum utilizador encontrado</p>
              <p className="mt-1 text-sm text-slate-500">Experimente alterar os termos da pesquisa.</p>
            </div>
          )}

          {filteredUsers.length > ROWS_PER_PAGE && (
            <div className="flex items-center justify-between border-t border-slate-100 px-5 py-4">
              <p className="text-sm text-slate-500">Página {currentPage} de {totalPages}</p>
              <div className="flex gap-2">
                <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 disabled:opacity-40">
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => page + 1)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 disabled:opacity-40">
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
                <ExclamationTriangleIcon className="h-6 w-6" />
              </span>
              <button type="button" onClick={() => setSelectedUser(null)} className="text-slate-400 hover:text-slate-700">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <h2 className="mt-5 text-xl font-extrabold text-slate-950">
              {selectedUser.deleted ? 'Ativar conta?' : 'Inativar conta?'}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Pretende {selectedUser.deleted ? 'devolver o acesso a' : 'retirar o acesso de'} <strong>{selectedUser.userEmail}</strong>?
            </p>
            <label className={`mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border p-4 ${includeForms?'border-red-300 bg-red-50':'border-slate-200 bg-slate-50'}`}>
              <input type="checkbox" checked={includeForms} onChange={event=>setIncludeForms(event.target.checked)} className="mt-1 rounded border-slate-300 text-red-600 focus:ring-red-500"/>
              <span><strong className="block text-sm text-slate-900">{selectedUser.deleted?'Ativar também todos os formulários':'Bloquear também todos os formulários'}</strong><small className="mt-1 block leading-5 text-slate-500">{selectedUser.deleted?'Reabre os formulários associados a esta conta.':'Botão de emergência: suspende imediatamente todos os formulários desta conta e impede novas respostas.'}</small></span>
            </label>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setSelectedUser(null)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700">
                Cancelar
              </button>
              <button type="button" onClick={handleConfirmStatus} className={`rounded-xl px-4 py-2.5 text-sm font-bold text-white ${!selectedUser.deleted&&includeForms?'bg-red-700 hover:bg-red-800':'bg-green-cl hover:bg-green-eupago'}`}>
                {!selectedUser.deleted&&includeForms?'Bloquear tudo':'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {notice && (
        <div className={`fixed bottom-5 right-5 z-50 flex items-center gap-3 rounded-2xl border px-5 py-4 text-sm font-semibold shadow-xl ${
          notice.type === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
        }`}>
          <span>{notice.message}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Fechar"><XMarkIcon className="h-5 w-5" /></button>
        </div>
      )}
    </main>
  );
};

export default AdminUsers;
